import {
  AvatarGroup,
  Box,
  Card,
  CardActionArea,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { MoreVertical } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ProjectIcon } from "../../common/components/ProjectIcon";
import { UserAvatar } from "../../common/components/UserAvatar";
import { useAuth } from "../../common/contexts/AuthContext";

function ProjectCard({ project, handleSwitchProject, handleDuplicate }) {
  const { t } = useTranslation("projects");
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const handleDuplicateClick = (event) => {
    event.stopPropagation();
    handleMenuClose(event);
    handleDuplicate(project.id);
  };

  const canSeeOtherMembers = () => {
    if (getCurrentUserRole() === "owner") {
      return true;
    }
    return project.members_can_see_other_members;
  };

  const getCurrentUserRole = () => {
    const currentUserInProject = project?.users?.find(
      (projectUser) => projectUser.user_details?.id === user.id,
    );

    return currentUserInProject?.role || null;
  };

  return (
    <Grid size={{ xs: 12, md: 4 }}>
      <Card
        sx={{
          height: "100%",
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: (theme) =>
              theme.palette.mode === "light"
                ? "0px 4px 20px rgba(0, 0, 0, 0.08)"
                : "0px 4px 20px rgba(0, 0, 0, 0.3)",
          },
        }}
        onClick={() => handleSwitchProject(project.id)}
      >
        <CardActionArea
          sx={{
            height: "100%",
            p: 3,
          }}
        >
          <Stack
            spacing={2}
            sx={{
              height: "100%",
              justifyContent: "space-between",
            }}
          >
            {/* Header with icon, title, and menu */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                position: "relative",
              }}
            >
              <ProjectIcon name={project.name} icon={project.icon} size={40} />
              <Typography
                variant="h3"
                sx={{
                  lineHeight: 1.3,
                  flex: 1,
                }}
              >
                {project.name}
              </Typography>
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                }}
              >
                <MoreVertical size={18} />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
              >
                <MenuItem onClick={handleDuplicateClick}>
                  {t("projectCard.duplicate")}
                </MenuItem>
              </Menu>
            </Box>

            {/* Description */}
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                lineHeight: 1.5,
                flex: 1,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {project.description}
            </Typography>

            {/* Members avatars */}
            {canSeeOtherMembers() && project?.users?.length > 0 && (
              <Box sx={{ mt: "auto" }}>
                <AvatarGroup
                  max={5}
                  sx={{
                    "& .MuiAvatar-root": {
                      width: 28,
                      height: 28,
                      fontSize: "0.75rem",
                      border: (theme) =>
                        `2px solid ${theme.palette.background.paper}`,
                    },
                    "& .MuiAvatarGroup-avatar": {
                      backgroundColor: (theme) => theme.palette.primary.main,
                      fontSize: "0.75rem",
                    },
                  }}
                >
                  {project.users.map((projectUser) => (
                    <UserAvatar
                      key={projectUser.id}
                      name={projectUser?.user_details?.name}
                      image={projectUser?.user_details?.avatar}
                      size={28}
                    />
                  ))}
                </AvatarGroup>
              </Box>
            )}
          </Stack>
        </CardActionArea>
      </Card>
    </Grid>
  );
}

export default ProjectCard;
